import {
  BRIEF_ATTACHMENT_EXTENSIONS,
  PRIVATE_FILES_ROUTE,
} from "./brief-attachments";
import type { BriefAttachment } from "./enums";

/** Folder chat attachments are uploaded under in the private GCS bucket. */
export const CHAT_ATTACHMENT_FOLDER = "chat-attachments";

/** Max size for a single deal-memo chat attachment. */
export const MAX_CHAT_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Longest file name a chat attachment may be stored under. */
export const MAX_CHAT_ATTACHMENT_NAME_LENGTH = 255;

/**
 * Whether a sender-supplied file name may be stored on a chat attachment:
 * non-empty, within the length cap, and ending in one of the accepted
 * extensions, so a saved copy can't land on disk as e.g. `.html` or `.exe`.
 * The extension isn't matched against the content type: browsers report
 * CSVs as Excel on some systems.
 */
export const isAllowedChatAttachmentName = (name: string): boolean => {
  const trimmed = name.trim();
  if (!trimmed || trimmed.length > MAX_CHAT_ATTACHMENT_NAME_LENGTH) {
    return false;
  }
  const lower = trimmed.toLowerCase();
  return BRIEF_ATTACHMENT_EXTENSIONS.some((extension) =>
    lower.endsWith(extension),
  );
};

/**
 * One file attached to a deal-memo chat message: the object key it was
 * uploaded under, the file name the sender gave it, and the store it is in.
 * Same shape as a brief attachment; both live in the same private bucket.
 */
export type ChatAttachment = BriefAttachment;

/**
 * Deal memo ids from `generateUUID()` are 12-char nanoids (e.g. `bv0ih3yadsh4`),
 * not RFC UUIDs. The file segment is a UUID v4 issued at upload time.
 */
const DEAL_MEMO_UUID_PATTERN = "[0-9a-z]{12}";
const FILE_UUID_PATTERN =
  "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

const CHAT_ATTACHMENT_KEY_PATTERN = new RegExp(
  `^${CHAT_ATTACHMENT_FOLDER}/(${DEAL_MEMO_UUID_PATTERN})/(${FILE_UUID_PATTERN})$`,
  "i",
);

/**
 * Whether an object key was issued for a chat attachment upload. When
 * `dealMemoUuid` is passed, the key must also belong to that deal memo.
 */
export const isChatAttachmentKey = (
  key: string,
  dealMemoUuid?: string,
): boolean => {
  const parsed = parseChatAttachmentKey(key);
  if (!parsed) {
    return false;
  }
  return (
    !dealMemoUuid ||
    parsed.dealMemoUuid.toLowerCase() === dealMemoUuid.toLowerCase()
  );
};

/** Deal memo uuid and file uuid embedded in a chat attachment key. */
export const parseChatAttachmentKey = (
  key: string,
): { dealMemoUuid: string; fileUuid: string } | null => {
  const match = key.match(CHAT_ATTACHMENT_KEY_PATTERN);
  if (!match) {
    return null;
  }
  return { dealMemoUuid: match[1], fileUuid: match[2] };
};

/**
 * The link that opens a chat attachment. The file is private, so the link
 * goes through the app's files route, which redirects to a short-lived signed
 * URL after checking the viewer can access the deal memo. The file name is
 * passed as a query so the signed URL can set Content-Disposition without a
 * separate metadata lookup.
 */
export const chatAttachmentHref = (attachment: ChatAttachment): string =>
  `${PRIVATE_FILES_ROUTE}/${attachment.id}?name=${encodeURIComponent(attachment.name)}`;
